import consumer from "../channels/consumer"

// The signaling transport shared by the beam operator console (beam_teleop_controller)
// and the Live-Ansicht (beam_live_view_controller): one ActionCable subscription on
// Beam::SignalingChannel per session, the browser->bridge `signal` perform, and the
// bridge->browser answer/ICE handling. Both controllers used to carry their own copy;
// a fix here (the `rejected` handling, the timeout) now reaches both.

// How long to wait for the server to confirm the subscription. ActionCable never
// times a subscribe out on its own: a socket that connects but a channel that
// neither confirms nor rejects would otherwise hang the caller forever.
const SUBSCRIBE_TIMEOUT_MS = 10000

// Subscribes to the signaling channel for `sessionId`. Returns the subscription right
// away (so a teardown can unsubscribe it even while it is still pending) together
// with a promise that resolves once the server confirmed it — the moment a `perform`
// (the SDP offer) is safe: ActionCable replays subscriptions on (re)connect but NOT
// one-shot performs, so an offer sent earlier is silently dropped. The promise rejects
// when the channel rejects (session ended meanwhile, token refused) or on timeout; a
// caller that only waited for `connected` would otherwise sit in "connecting" forever.
//
// ActionCable re-subscribes on its own after a cable drop, and the server may refuse
// that second subscription (a view session ends with its cable). That rejection comes
// long after `connected` settled, so it goes to `onLost` — without it the caller would
// never learn that its signaling is gone.
// A per-page identity for the signaling channel. The same account may open a session's
// page twice (a control session resumes from any tab); the server hands the session to
// the page that offered last and tells the others `replaced` — each page recognises its
// own id in that notice and ignores it. See Beam::SignalingChannel#take_over_signaling.
export function newPeerId() {
  return crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)
}

export function subscribeSignaling(
  channel,
  sessionId,
  { peer, received, onLost, timeoutMs = SUBSCRIBE_TIMEOUT_MS },
) {
  let subscription
  let confirmed = false
  const connected = new Promise((resolve, reject) => {
    let settled = false
    const settle = (finish, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      finish(value)
    }
    const timer = setTimeout(
      () => settle(reject, new Error("signaling subscription timed out")),
      timeoutMs,
    )
    subscription = consumer.subscriptions.create(
      { channel, session_id: sessionId, peer },
      {
        // Fires again on every cable reconnect; only the first confirmation settles.
        connected: () => {
          confirmed = true
          settle(resolve)
        },
        rejected: () => {
          if (confirmed) onLost?.()
          else settle(reject, new Error("signaling subscription rejected"))
        },
        received,
      },
    )
  })
  return { subscription, connected }
}

// Applies a bridge->browser signaling message to the peer connection. Returns true
// when the message was an answer or an ICE candidate, false for anything else so the
// caller can handle its own message types (the teleop console's `status`, `replaced`).
//
// An answer is only applied while this peer connection is waiting for one. Anything
// else on the wire in that slot is not ours: the stream is per session, and a second
// page of the same session may have offered meanwhile (the server tells us so with
// `replaced`, which may arrive after the stray answer). Feeding it to a stable
// connection is the "Called in wrong state: stable" InvalidStateError.
export async function applyRemoteSignal(pc, data) {
  if (data.type === "answer") {
    if (pc.signalingState !== "have-local-offer") {
      console.warn("[beam] ignoring answer in signaling state", pc.signalingState)
      return true
    }
    try {
      await pc.setRemoteDescription(new RTCSessionDescription(data.payload))
    } catch (error) {
      console.error("[beam] setRemoteDescription failed", error)
    }
    return true
  }
  if (data.type === "ice") {
    if (!data.payload || pc.signalingState === "closed") return true
    try {
      await pc.addIceCandidate(new RTCIceCandidate(data.payload))
    } catch (error) {
      console.error("[beam] addIceCandidate failed", error)
    }
    return true
  }
  return false
}

export function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content
}
